import React, { useEffect, useRef } from 'react'
import { icon } from '@fortawesome/fontawesome-svg-core'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { genericError } from './useRecord'

// PayPal / Venmo SDK button. Adapted from donate_v6/fiat/Summary's button
// effect, but kept mounted across tip / cover-costs / amount changes:
// stateRef reads the current values when the SDK calls createOrder
// (which only happens lazily on the user's click). The SDK itself comes
// from usePaypalSdk so the same instance services both the venmo
// eligibility probe and the rendered button — see usePaypalSdk for why.
const PaypalButton = ({
  i18n, donation, paypal, paypalConfig, partnerItem, paymentMethod, paypalSdk,
}) => {
  const buttonRef = useRef()

  const stateRef = useRef()
  stateRef.current = {
    total: donation.total,
    tip: donation.tip,
    currency: donation.currency.iso_code,
    partnerItemId: partnerItem.id,
    validateTip: donation.validateTip,
  }

  // The SDK keeps the callbacks it was handed when the buttons were built,
  // and a tip or cover-costs change doesn't rebuild them. paypal.process
  // saves the draft from the donation of the render it came from, so
  // calling the one captured at build time recorded the old amounts while
  // PayPal charged createOrder's current total. The callbacks go through
  // this ref to reach the latest render's handlers instead.
  const paypalRef = useRef()
  paypalRef.current = paypal

  const isSubscription = donation.state.subscription !== 'one_time'

  useEffect(() => {
    let buttons

    const clearButton = () => {
      if (!buttonRef.current) return
      while (buttonRef.current.firstChild)
        buttonRef.current.removeChild(buttonRef.current.firstChild)
    }

    const insertSpinner = () => {
      const spinner = icon(faCircleNotch, { classes: 'fa-2x fa-spin' })
      buttonRef.current?.insertAdjacentHTML(
        'beforeend',
        `<div class="text-center" style="color: #0079c1;">${spinner.html}</div>`
      )
    }

    clearButton()

    if (!paypalSdk || !paypal.ready) {
      insertSpinner()
      return
    }

    // paymentToken is the vault token on the recurring path. With it,
    // PaypalController#create charges the first month on the spot; without
    // it, the order waits for a buyer approval nothing ever asks for.
    const createOrder = async paymentToken => {
      const { total, tip, currency, partnerItemId } = stateRef.current
      const response = await fetch(paypalConfig.create_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total,
          tip,
          currency,
          partner_item_id: partnerItemId,
          payment_token: paymentToken,
        }),
      })
      if (!response.ok) throw new Error('Failed to create PayPal order')
      const { id } = await response.json()
      return id
    }

    const createVaultSetupToken = async () => {
      const response = await fetch(paypalConfig.vault_setup_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) throw new Error('Failed to create PayPal vault setup token')
      const { id } = await response.json()
      return id
    }

    const onApproveVault = async data => {
      const response = await fetch(paypalConfig.vault_approve_path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vaultSetupToken: data.vaultSetupToken }),
      })
      if (!response.ok) throw new Error('Failed to approve PayPal vault')
      const result = await response.json()

      const orderId = await createOrder(result.id)

      return paypalRef.current.process({
        orderID: orderId,
        paymentToken: result.id,
        customerId: result.customer?.id,
      })
    }

    const onError = error => {
      console.error('PayPal error:', error)
      paypalRef.current.setErrors(genericError(i18n))
    }

    // PayPal SDK calls this before opening the popup. actions.reject()
    // surfaces no PayPal-side UI, leaving the OrderSummary's inline
    // error (set by validateTip → setErrors) as the only feedback.
    const onClick = (data, actions) =>
      stateRef.current.validateTip()
        ? actions.resolve()
        : actions.reject()

    const fundingSource = paypalSdk.FUNDING[paymentMethod.toUpperCase()]

    // Match v7's regular .btn: 44px tall, 8px radius. PayPal accepts
    // borderRadius in pixels; height clamps within 25-55.
    const style = { height: 44, borderRadius: 8 }

    buttons = isSubscription
      ? paypalSdk.Buttons({
        fundingSource,
        style,
        onClick,
        createVaultSetupToken,
        onApprove: onApproveVault,
        onError,
      })
      : paypalSdk.Buttons({
        fundingSource,
        style,
        onClick,
        // The SDK calls this with (data, actions), which mustn't reach
        // createOrder as a vault token.
        createOrder: () => createOrder(),
        onApprove: data => paypalRef.current.process(data),
        onError,
      })

    buttons.render(buttonRef.current)

    return () => buttons?.close()
  }, [ paypalSdk, paypal.ready, paymentMethod, isSubscription ])

  return <div ref={buttonRef} className="grid" />
}

export default PaypalButton
